<template>
    <CRow>
        <CCol col="12">
            <CCard>
                <CCardHeader>
                    <img
                        class="d-none showOnPrint"
                        src="../../../public/logo_1.png"
                        alt=""
                        id="print_logo"
                    />
                    <div class="d-none showOnPrint"><h2>{{ customer.customerId }}: {{ customer.customerName }}</h2></div>
                    <h3 class="float-left">Payment List</h3>
                    <CButton
                        class="float-right hideOnPrint"
                        color="secondary"
                        @click="$router.go(-1)"
                        ><CIcon name="cilChevronLeft" />
                        Back
                    </CButton>
                </CCardHeader>
                <CCardBody>
                    <CRow>
                        <CCol md="3">
                            <label>From Date</label>
                            <datepicker
                                v-model="fromDate"
                                :format="dateFormat"
                            ></datepicker>
                        </CCol>
                        <CCol md="3">
                            <label>To Date</label>
                            <datepicker
                                v-model="toDate"
                                :format="dateFormat"
                            ></datepicker>
                        </CCol>
                        <CCol md="1">
                            <CButton
                                class="filterBtn hideOnPrint"
                                type="submit"
                                color="primary"
                                @click="filterData"
                            >
                                Filter</CButton
                            >
                        </CCol>
                        <CCol md="5">
                            <CAlert color="warning m-2" v-if="limitReached">
                                Warning! You have reached the limit of {{ limit }} records. Please adjust your filters to see full results.
                            </CAlert>
                            <div v-else id="payment-list-totals" class="px-2 pt-1 pb-2 border border-secondary rounded">
                                <table class="w-100">
                                    <tr>
                                        <th class="text-right">Total</th>
                                        <th class="text-right">Invoice</th>
                                        <th class="text-right">Payment</th>
                                    </tr>
                                    <tr>
                                        <td class="text-right">{{ payments && payments.length }}</td>
                                        <td class="text-right">{{ cumulatedInvoiceTotal | currency }}</td>
                                        <td class="text-right">{{ cumulatedPaymentTotal | currency }}</td>
                                    </tr>
                                </table>
                            </div>
                        </CCol>
                    </CRow>

                    <CDataTable
                        class="datatable"
                        hover
                        striped
                        clickable-rows
                        :sorter="{ external: false, resetable: true }"
                        :sorterValue="{ column: 'date', asc: false }"
                        :items="payments"
                        :fields="fields"
                        :items-per-page="pageLimit"
                        :itemsPerPageSelect="{ class: 'pageLimit' }"
                        :active-page="activePage"
                        :columnFilter="{ external: false, lazy: false }"
                        @row-clicked="rowClicked"
                        @pagination-change="paginationChanged"
                    >
                        <template #over-table>
                            <CButton
                                class="float-right px-4 mb-1 ml-1 hideOnPrint"
                                color="primary"
                                :disabled="!payments || payments.length == 0"
                                @click="printReport"
                            >
                                <CIcon name="cil-print" />
                                Print Statement
                            </CButton>
                            <CButton
                                class="float-right px-4  mb-1 hideOnPrint"
                                color="primary"
                                :disabled="!payments || payments.length == 0"
                            >
                                <download-csv :data="payments || []">
                                    <CIcon name="cil-save" />
                                    Download Data
                                </download-csv>
                            </CButton>
                        </template>
                        <template #total="data">
                            <td class="text-right">{{ data.item.total | currency }}</td>
                        </template>
                        <template #paymentTotal="data">
                            <td class="text-right">{{ data.item.paymentTotal | currency }}</td>
                        </template>
                    </CDataTable>

                    <DataTablePagination
                        :activePage="activePage"
                        :total="payments && payments.length || 0"
                        :limit="pageLimit"
                        @page-changed="pageChanged"
                    />
                </CCardBody>
            </CCard>
        </CCol>
    </CRow>
</template>

<script>
/*
 * PaymentList Vue.js view module for the payment list history for Suncast
 * Display the history of previous payments for the logged in client
 * @module PaymentList
 * @author Kevin Jones (Fresche Solutions)
 * @date July 2020
 */
import Payments from "../../store/modules/Payments";
import User from "../../../../services/user-store";
import Datepicker from "vuejs-datepicker";
import DataTablePagination from "../components/DataTablePagination";
import JsonCSV from "vue-json-csv";
import { mapState, mapMutations, mapActions, mapGetters } from "vuex";

export default {
    name: "PaymentList",
    components: {
        downloadCsv: JsonCSV,
        Datepicker,
        DataTablePagination,
    },
    created: function() {
        this.init();
    },
    data() {
        return {
            customer: '',

            limit: 0,
            pageLimit: 0,
            activePage: 1,
            limitReached: false,

            dateFormat: "yyyy-MM-dd",
            fromDate: null,
            toDate: null,
            payments: null,
            fields: [
                {
                    key: "id",
                    label: "Payment ID",
                },
                {
                    key: "date",
                    label: "Payment Date",
                },
                {
                    key: "status",
                    label: "Status",
                },
                {
                    key: "total",
                    label: "Invoice Total",
                    _classes: "text-right",
                },
                {
                    key: "paymentTotal",
                    label: "Payment Total",
                    _classes: "text-right",
                },
            ]
        };
    },
    computed: {
        cumulatedInvoiceTotal() {
            return this.getCumulatedInvoiceTotal();
        },
        cumulatedPaymentTotal() {
            return this.getCumulatedPaymentTotal();
        },
    },
    async mounted() {
        this.pageLimit = this.getPageLimit();

        // Get the customer name for printing
        this.customer = await this.getSelectedCustomer();
    },
    watch: {
        $route: {
            immediate: true,
            handler(route) {
                this.onView();
            }
        },
        $route: {
            immediate: true,
            handler(route) {
                if (route.query && route.query.page) {
                    this.activePage = Number(route.query.page);
                }
            }
        }
    },
    methods: {
        ...mapMutations("Payments", ["setFromDate", "setToDate", "setPageLimit", "setSelectedPayment"]),
        ...mapActions("Payments", ["fetchPayments"]),
        ...mapGetters("Payments", ["getPayments", "getFromDate", "getToDate", "getRecordLimit", "getPageLimit", "getCumulatedInvoiceTotal", "getCumulatedPaymentTotal"]),
        ...mapGetters("User", ["getSelectedCustomer"]),

        async init() {
            // Update the redirect from route, if provided
            if (this.$route.params.redirect) {
                this.redirectTo = this.$route.params.redirect;
            }

            // Get last used date filters from store
            this.fromDate = this.getFromDate();
            this.toDate = this.getToDate();

            // If the dates are null, set default
            if (!this.fromDate || !this.toDate) {

                // Set default "from" date filter to one month ago
                this.fromDate = new Date();
                this.fromDate.setMonth(this.fromDate.getMonth() - 1);
                this.setFromDate(this.fromDate);

                // Set default "to" date filter to current date
                this.toDate = new Date();
                this.setToDate(this.toDate);
            }

            // Get the payment data
            this.getInvoicePayments();
        },

        filterData() {

            // Set the filtered dates
            this.setFromDate(this.fromDate);
            this.setToDate(this.toDate);

            // Refresh the payment data
            this.getInvoicePayments(true);
        },

        async getInvoicePayments(fetchNew = false) {

            let paymentList = this.getPayments();

            // Only fetch payments by date range filters or if we haven't fetched them yet
            if (fetchNew || !paymentList || paymentList.length == 0) {

                let result = await this.fetchPayments({
                    fromDate: this.fromDate,
                    toDate: this.toDate
                });

                // Failed - show errors that were returned in result & return
                if (result !== true) {
                    let keys = Object.keys(result);
                    for (const key of keys) {
                        this.$toast.open({
                            message: result[key],
                            type: "error"
                        });
                    }
                    return;
                }

                paymentList = await this.getPayments();
            }

            // Update the payments list
            this.payments = paymentList;

            // Check if we've hit the maximum number of records
            this.limitReached = false;
            if (paymentList && paymentList.length > 0) {
                this.limit = this.getRecordLimit();
                this.limitReached = (paymentList.length == this.limit);
            }
        },

        rowClicked(payment) {
            this.setSelectedPayment(payment);
            this.$router.push({ path: `/payments/display/${payment.id}/${payment.date}` });
        },

        printReport() {
            window.print();
        },

        // Change & save the page limit
        paginationChanged(pageLimit) {
            this.setPageLimit(pageLimit);
            this.pageLimit = pageLimit;
        },

        // Data table page change
        pageChanged(newPage) {

            // Get the page we're currently on
            let query = this.$router.currentRoute.query;

            // If it's different, navigate & set the active page
            if (!query.page || query.page != newPage) {
                this.activePage = newPage;
                this.$router.push({ query: { page: newPage } });
            }
        },
    }
};
</script>

<style scoped>
    .filterBtn {
        margin-top: 25px;
    }
</style>
